import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader2, AlertTriangle, CheckCircle, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_STYLES = {
  exito: 'bg-green-100 text-green-800 border-green-300',
  token: 'bg-amber-100 text-amber-800 border-amber-300',
  fondos: 'bg-orange-100 text-orange-800 border-orange-300',
  datos_pagador: 'bg-sky-100 text-sky-800 border-sky-300',
  cuenta_pagador: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  integracion: 'bg-red-100 text-red-800 border-red-300',
  banco: 'bg-slate-100 text-slate-700 border-slate-300',
  proceso: 'bg-purple-100 text-purple-800 border-purple-300',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export default function DIAttemptsAudit({ setToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyFailed, setOnlyFailed] = useState(true);
  const [codigo, setCodigo] = useState('');
  const [expanded, setExpanded] = useState(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (onlyFailed) params.set('only_failed', 'true');
      if (codigo.trim()) params.set('codigo', codigo.trim());
      const res = await axios.get(
        `${BACKEND_URL}/api/super-admin/debito-inmediato/attempts?${params.toString()}`,
        authHeaders(),
      );
      setRows(res.data?.attempts || []);
    } catch (err) {
      setToast?.({ type: 'error', message: err.response?.data?.detail || 'No se pudo cargar la auditoría de débito inmediato' });
    } finally {
      setLoading(false);
    }
  }, [onlyFailed, codigo, setToast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border rounded-lg overflow-hidden" data-testid="di-attempts-audit">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Auditoría de Débito Inmediato</h3>
          <p className="text-xs text-slate-500 mt-0.5">Respuesta literal del banco en cada intento de cobro, con el mensaje accionable para el pagador.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} data-testid="di-audit-only-failed" />
            Solo rechazados
          </label>
          <div className="relative">
            <Filter className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="código (0002)"
              className="pl-7 pr-2 py-1 border rounded text-xs font-mono w-32"
              data-testid="di-audit-codigo-filter"
            />
          </div>
          <button onClick={load} className="px-3 py-1.5 text-xs border rounded hover:bg-slate-50 flex items-center gap-1" data-testid="di-audit-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Actualizar
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="di-audit-empty">
          {loading ? 'Cargando…' : 'Sin intentos registrados con estos filtros.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase">Fecha</th>
                <th className="px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase">Orden</th>
                <th className="px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase">Paso</th>
                <th className="px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase">Código</th>
                <th className="px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase">Respuesta del banco</th>
                <th className="px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.attempt_id} className="border-t align-top hover:bg-slate-50" data-testid={`di-attempt-${r.attempt_id}`}>
                  <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="px-4 py-2 text-xs font-mono">{r.order_code || '—'}</td>
                  <td className="px-4 py-2 text-xs">{r.step === 'execute' ? 'Cobro' : 'Token SMS'}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono border ${CATEGORY_STYLES[r.categoria] || CATEGORY_STYLES.banco}`}>
                      {r.codigo || '—'}
                    </span>
                    {r.success
                      ? <CheckCircle className="w-3.5 h-3.5 text-green-600 inline ml-1.5" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-amber-600 inline ml-1.5" />}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-700 max-w-md">
                    <div className="font-medium">{r.descripcion_banco || '—'}</div>
                    {r.descripcion_sistema && <div className="text-[11px] text-slate-500 mt-0.5">{r.descripcion_sistema}</div>}
                    {expanded === r.attempt_id && (
                      <pre className="mt-2 p-2 bg-slate-900 text-slate-200 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify({ request: r.request_payload, response: r.bank_response }, null, 1)}
                      </pre>
                    )}
                    <button
                      onClick={() => setExpanded(expanded === r.attempt_id ? null : r.attempt_id)}
                      className="mt-1 text-[11px] text-blue-600 hover:underline"
                      data-testid={`di-attempt-toggle-${r.attempt_id}`}
                    >
                      {expanded === r.attempt_id ? 'Ocultar payload' : 'Ver payload'}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-xs">{r.mensaje_pagador}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
